import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@ibe/ui';
import { useState } from 'react';

import { useAppForm } from '../../../lib/forms/use-app-form.js';
import {
  AMENITY_DEFAULTS,
  amenities,
  amenityFormInput,
  AmenityFields,
} from '../../amenities/index.js';
import { type AmenityFormInput } from '../../amenities/index.js';
import {
  NOTIFICATION_GATEWAY_DEFAULTS,
  gatewayFormInputCreate,
  NotificationGatewayFields,
  useCreateGateway,
} from '../../notification-gateways/index.js';
import { type GatewayFormInput } from '../../notification-gateways/index.js';
import {
  PAYMENT_GATEWAY_DEFAULTS,
  paymentGatewayFormInput,
  PaymentGatewayFields,
  useCreatePaymentGateway,
} from '../../payment-gateways/index.js';
import { type PaymentGatewayFormInput } from '../../payment-gateways/index.js';

export interface CreatedNotificationGateway {
  readonly id: string;
  readonly name: string;
  readonly channel: string;
}

export interface CreateNotificationGatewayDialogProps {
  readonly children: React.ReactNode;
  readonly onCreated: (gateway: CreatedNotificationGateway) => void;
}

export function CreateNotificationGatewayDialog({
  children,
  onCreated,
}: CreateNotificationGatewayDialogProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const create = useCreateGateway();

  const form = useAppForm<GatewayFormInput>({
    schema: gatewayFormInputCreate,
    defaultValues: NOTIFICATION_GATEWAY_DEFAULTS,
    successMessage: 'Gateway created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      onCreated({ id: created.id, name: values.name, channel: values.channel });
    },
    onSuccess: () => {
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New notification gateway</DialogTitle>
          <DialogDescription>
            Add a notification gateway to use in this property setup.
          </DialogDescription>
        </DialogHeader>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
          className="space-y-6"
        >
          <NotificationGatewayFields
            register={form.register}
            errors={form.formState.errors}
            control={form.control}
            setValue={form.setValue}
            mode="create"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={form.submitting}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.submitting}>
              {form.submitting ? 'Creating…' : 'Create gateway'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface CreatedPaymentGateway {
  readonly id: string;
  readonly name: string;
}

export interface CreatePaymentGatewayDialogProps {
  readonly children: React.ReactNode;
  readonly onCreated: (gateway: CreatedPaymentGateway) => void;
}

export function CreatePaymentGatewayDialog({
  children,
  onCreated,
}: CreatePaymentGatewayDialogProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const create = useCreatePaymentGateway();

  const form = useAppForm<PaymentGatewayFormInput>({
    schema: paymentGatewayFormInput,
    defaultValues: PAYMENT_GATEWAY_DEFAULTS,
    successMessage: 'Payment gateway created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      onCreated({ id: created.id, name: values.name });
    },
    onSuccess: () => {
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New payment gateway</DialogTitle>
          <DialogDescription>
            Add a payment gateway to use in this property setup.
          </DialogDescription>
        </DialogHeader>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
          className="space-y-6"
        >
          <PaymentGatewayFields
            register={form.register}
            errors={form.formState.errors}
            control={form.control}
            setValue={form.setValue}
            mode="create"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={form.submitting}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.submitting}>
              {form.submitting ? 'Creating…' : 'Create gateway'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface CreatedAmenity {
  readonly id: string;
  readonly name: string;
}

export interface CreateAmenityDialogProps {
  readonly children: React.ReactNode;
  readonly onCreated: (amenity: CreatedAmenity) => void;
}

export function CreateAmenityDialog({
  children,
  onCreated,
}: CreateAmenityDialogProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const create = amenities.useCreate();

  const form = useAppForm<AmenityFormInput>({
    schema: amenityFormInput,
    defaultValues: AMENITY_DEFAULTS,
    successMessage: 'Amenity created',
    onSubmit: async (values) => {
      const result = await create.mutateAsync({ ...values, iconUrl: values.iconUrl || null });
      onCreated({ id: result.id, name: values.name });
    },
    onSuccess: () => {
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New amenity</DialogTitle>
          <DialogDescription>Add an amenity to use in this property setup.</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
          className="space-y-6"
        >
          <AmenityFields
            register={form.register}
            errors={form.formState.errors}
            control={form.control}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={form.submitting}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.submitting}>
              {form.submitting ? 'Creating…' : 'Create amenity'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
